import { supabase } from "@/lib/supabase";

export interface ModuleAccess {
  key: string;
  name: string;
  tier: string;
  sortOrder: number;
  /** Lo tiene contratado y funcionando. */
  contratado: boolean;
  /** Le corresponde por su plan, aunque todavia no se le haya activado. */
  enSuPlan: boolean;
}

export interface FeatureAccess {
  key: string;
  moduleKey: string;
  name: string;
  description: string | null;
  incluida: boolean;
}

export interface PlanInfo {
  code: string;
  name: string;
  maxAttributes: number | null;
}

/**
 * Todos los modulos, contratados o no.
 *
 * Esta es la diferencia con `fetchModules`, que solo devuelve los activos: el
 * panel necesita conocer tambien los que le faltan para poder enseñarlos con
 * candado. Que se vean no los habilita — cada funcion del modulo comprueba
 * `app.module_enabled()` por su cuenta, y ahi no llega ninguna decision de
 * interfaz.
 */
export async function fetchModuleAccess(organizationId: string): Promise<ModuleAccess[]> {
  const { data, error } = await supabase
    .from("organization_module_access")
    .select("key, name, tier, sort_order, contratado, en_su_plan")
    .eq("organization_id", organizationId)
    .eq("available", true)
    .order("sort_order");

  if (error) throw error;

  return (data ?? []).map((m) => ({
    key: m.key!,
    name: m.name!,
    tier: m.tier ?? "base",
    sortOrder: m.sort_order ?? 0,
    contratado: Boolean(m.contratado),
    enSuPlan: Boolean(m.en_su_plan),
  }));
}

export async function fetchFeatureAccess(organizationId: string): Promise<FeatureAccess[]> {
  const { data, error } = await supabase
    .from("organization_feature_access")
    .select("key, module_key, name, description, incluida, sort_order")
    .eq("organization_id", organizationId)
    .order("sort_order");

  if (error) throw error;

  return (data ?? []).map((f) => ({
    key: f.key!,
    moduleKey: f.module_key!,
    name: f.name!,
    description: f.description,
    incluida: Boolean(f.incluida),
  }));
}

/**
 * Dos consultas en vez de un join anidado: `organizations.plan` es una clave
 * foranea a `plans.code`, no a su id, y PostgREST no la reconoce como
 * relacion. Insistir con el join solo daria un error mas raro de leer.
 */
export async function fetchPlan(organizationId: string): Promise<PlanInfo | null> {
  const { data: org, error } = await supabase
    .from("organizations")
    .select("plan")
    .eq("id", organizationId)
    .single();

  if (error) throw error;
  if (!org?.plan) return null;

  const { data: plan, error: planError } = await supabase
    .from("plans")
    .select("code, name, max_attributes")
    .eq("code", org.plan)
    .maybeSingle();

  if (planError) throw planError;
  if (!plan) return null;

  return { code: plan.code, name: plan.name, maxAttributes: plan.max_attributes };
}
